import React, { useState, useEffect, useCallback } from 'react';
import { CalendarRange, RefreshCw, Send, CheckCheck, Megaphone, Eye } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchMonthlyCampaignOverview } from '../lib/campaignService';

const DELIVERY_COLOR = '#5D27CA';
const READ_COLOR = '#22B07D';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

function formatMonthLabel(key) {
  const [y, m] = String(key || '').split('-').map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

function RateTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="campaign-monthly-tooltip">
      <p className="campaign-monthly-tooltip-label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="campaign-monthly-tooltip-row">
          <span className="campaign-monthly-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{p.value}%</strong>
        </p>
      ))}
    </div>
  );
}

function CountTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="campaign-monthly-tooltip">
      <p className="campaign-monthly-tooltip-label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="campaign-monthly-tooltip-row">
          <span className="campaign-monthly-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{fmt(p.value)}</strong>
        </p>
      ))}
    </div>
  );
}

export default function CampaignMonthlyTab({ apiUrl }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMonthlyCampaignOverview(apiUrl);
      setOverview(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load monthly campaign overview');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const months = overview?.months || [];
  // months arrives newest-first from the API; reset to a valid choice whenever the
  // list changes (e.g. after a refresh) so a stale month key never lingers selected.
  useEffect(() => {
    if (selectedMonth !== 'all' && !months.some((m) => m.month === selectedMonth)) {
      setSelectedMonth('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const selectedRow = selectedMonth === 'all' ? null : months.find((m) => m.month === selectedMonth);
  const displayedMonths = selectedRow ? [selectedRow] : months;
  const chronological = [...displayedMonths].reverse();

  const rateData = chronological.map((m) => ({
    month: formatMonthLabel(m.month),
    'Delivery rate': m.avg_delivery_rate,
    'Read rate': m.avg_read_rate,
  }));
  // Older API responses only had total_customers_reached (which is the delivered count).
  const deliveredOf = (m) => m?.total_delivered ?? m?.total_customers_reached ?? 0;
  const countData = chronological.map((m) => ({
    month: formatMonthLabel(m.month),
    Delivered: deliveredOf(m),
    Read: m.total_read ?? 0,
  }));

  const headline = selectedRow || overview;

  return (
    <div className="campaigns-section">
      <div className="campaigns-header">
        <h2><CalendarRange size={24} /> Monthly Overview</h2>
        <div className="campaigns-actions">
          <select
            className="campaign-month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            disabled={loading || months.length === 0}
          >
            <option value="all">All months</option>
            {months.map((m) => (
              <option key={m.month} value={m.month}>{formatMonthLabel(m.month)}</option>
            ))}
          </select>
          <button type="button" className="refresh-btn" onClick={load} disabled={loading}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {selectedRow && (
        <p className="campaign-manager-hint">
          {`Showing ${formatMonthLabel(selectedRow.month)} only. Switch back to "All months" for the full trend.`}
        </p>
      )}

      <div className="stats-grid campaign-overview-grid">
        <div className="stat-card">
          <Megaphone size={20} />
          <div className="stat-info">
            <span className="stat-number">{headline?.total_campaigns ?? 0}</span>
            <span className="stat-title">Total campaigns</span>
          </div>
        </div>
        <div className="stat-card">
          <Send size={20} />
          <div className="stat-info">
            <span className="stat-number">{fmt(headline?.total_recipients)}</span>
            <span className="stat-title">Messages sent</span>
          </div>
        </div>
        <div className="stat-card">
          <CheckCheck size={20} style={{ color: DELIVERY_COLOR }} />
          <div className="stat-info">
            <span className="stat-number">{fmt(deliveredOf(headline))}</span>
            <span className="stat-title">Delivered · {headline?.avg_delivery_rate ?? 0}% of sent</span>
          </div>
        </div>
        <div className="stat-card">
          <Eye size={20} style={{ color: READ_COLOR }} />
          <div className="stat-info">
            <span className="stat-number">{fmt(headline?.total_read)}</span>
            <span className="stat-title">Read · {headline?.avg_read_rate ?? 0}% of delivered</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading monthly overview…</div>
      ) : months.length === 0 ? (
        <div className="empty-state">
          <CalendarRange size={48} />
          <p>No sent campaigns yet</p>
          <span>Trigger a campaign from Campaign Manager and it'll show up here once it's sent.</span>
        </div>
      ) : (
        <>
          <div className="campaign-monthly-chart-card">
            <h3>Delivery &amp; read rate {selectedRow ? `— ${formatMonthLabel(selectedRow.month)}` : 'by month'}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={rateData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                <XAxis dataKey="month" stroke="var(--text-secondary)" fontSize={12} tickLine={false} />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  stroke="var(--text-secondary)"
                  fontSize={12}
                  tickLine={false}
                  width={44}
                />
                <Tooltip content={<RateTooltip />} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Line
                  type="monotone"
                  dataKey="Delivery rate"
                  stroke={DELIVERY_COLOR}
                  strokeWidth={2}
                  dot={{ r: 4, fill: DELIVERY_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="Read rate"
                  stroke={READ_COLOR}
                  strokeWidth={2}
                  dot={{ r: 4, fill: READ_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="campaign-monthly-chart-card">
            <h3>Delivered &amp; read messages {selectedRow ? `— ${formatMonthLabel(selectedRow.month)}` : 'by month'}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={countData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-color)" />
                <XAxis dataKey="month" stroke="var(--text-secondary)" fontSize={12} tickLine={false} />
                <YAxis stroke="var(--text-secondary)" fontSize={12} tickLine={false} width={44} />
                <Tooltip content={<CountTooltip />} cursor={{ fill: 'rgba(93, 39, 202, 0.06)' }} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey="Delivered" fill={DELIVERY_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="Read" fill={READ_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="campaign-monthly-table-wrap">
            <h3>Month-by-month detail</h3>
            <table className="campaign-messages-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Campaigns</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Read</th>
                  <th>Delivery rate</th>
                  <th>Read rate</th>
                </tr>
              </thead>
              <tbody>
                {displayedMonths.map((m) => (
                  <tr key={m.month}>
                    <td>{formatMonthLabel(m.month)}</td>
                    <td>{m.total_campaigns}</td>
                    <td>{fmt(m.total_recipients)}</td>
                    <td>{fmt(deliveredOf(m))}</td>
                    <td>{fmt(m.total_read)}</td>
                    <td>{m.avg_delivery_rate}%</td>
                    <td>{m.avg_read_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
